import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineBillDetail } from 'api/types';
import { printBill } from '../cashier/billPrint';

/**
 * Read-only view of one bill for the reports: lines, tax by component,
 * round-off, receipts and the discount record. Nothing here mutates.
 */

const money = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const time = (iso: string) => new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

interface Props { billId: string; onClose: () => void; }

const DineOpsBillDetailModal: React.FC<Props> = ({ billId, onClose }) => {
  const [bill, setBill] = useState<DineBillDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    dineOpsService.getBill(billId)
      .then((b) => { if (alive) setBill(b); })
      .catch(() => notificationService.error('Not loaded', 'The bill could not be loaded.'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [billId]);

  const print = async () => {
    try { const p = await dineOpsService.getBillPrint(billId); if (!printBill(p)) notificationService.error('Popup blocked', 'Allow popups for this site to print.'); }
    catch { notificationService.error('Not printed', 'The invoice could not be loaded.'); }
  };

  const tone = bill?.billStatus === 'SETTLED' ? 'var(--color-success, #166534)' : bill?.billStatus === 'VOID' ? 'var(--color-danger, #991b1b)' : 'var(--color-warning, #92400e)';
  const row: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', fontSize: 13 };
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.6, padding: '6px 8px', borderBottom: '1px solid var(--border-color, #e5e7eb)' };
  const td: React.CSSProperties = { padding: '6px 8px', fontSize: 13, borderBottom: '1px solid var(--border-color, #f1f5f9)' };
  const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container" style={{ maxWidth: 820, minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconSystem.list size={18} /> {bill?.billNumber ?? 'Bill'}
            {bill && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, border: `1px solid ${tone}`, color: tone }}>{bill.billStatus === 'GENERATED' ? 'OPEN' : bill.billStatus}</span>}
          </h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading && <div style={{ opacity: 0.7, padding: 12 }}>Loading the bill…</div>}
          {!loading && bill && (
            <>
              {/* who / where / when */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, padding: '0 0 12px', fontSize: 12 }}>
                <div><div style={{ opacity: 0.6 }}>Order</div><div style={{ fontWeight: 600 }}>{bill.orderNumber}</div></div>
                <div><div style={{ opacity: 0.6 }}>Table / room</div><div style={{ fontWeight: 600 }}>{bill.roomNumber && !bill.tableNumber ? `Room ${bill.roomNumber}` : bill.tableNumber ?? '—'}</div></div>
                <div><div style={{ opacity: 0.6 }}>Customer</div><div style={{ fontWeight: 600 }}>{bill.customerType === 'IN_HOUSE' ? 'In-house guest' : bill.customerType === 'CORPORATE' ? 'Corporate' : 'Walk-in'}{bill.customerName ? ` · ${bill.customerName}` : ''}{bill.customerType === 'IN_HOUSE' && bill.customerRoom ? ` · Room ${bill.customerRoom}` : ''}{bill.customerPhone ? ` · ${bill.customerPhone}` : ''}{bill.customerGstin ? ` · ${bill.customerGstin}` : ''}</div></div>
                <div><div style={{ opacity: 0.6 }}>Generated</div><div style={{ fontWeight: 600 }}>{time(bill.generatedAt)}</div></div>
                {bill.settledAt && <div><div style={{ opacity: 0.6 }}>Settled</div><div style={{ fontWeight: 600 }}>{time(bill.settledAt)}{bill.settlementType ? ` · ${bill.settlementType}` : ''}</div></div>}
                {bill.voidReason && <div><div style={{ opacity: 0.6 }}>Void reason</div><div style={{ fontWeight: 600, color: tone }}>{bill.voidReason}</div></div>}
              </div>

              {/* lines */}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Item</th><th style={{ ...th, textAlign: 'right' }}>Qty</th><th style={{ ...th, textAlign: 'right' }}>Rate</th><th style={th}>Tax</th><th style={{ ...th, textAlign: 'right' }}>Amount</th></tr></thead>
                <tbody>
                  {bill.lines.map((l) => (
                    <tr key={l.id}>
                      <td style={td}>{l.itemName}{l.variantName ? <span style={{ opacity: 0.7 }}> ({l.variantName})</span> : null}{l.addons.length ? <div style={{ fontSize: 11, opacity: 0.6 }}>+ {l.addons.join(', ')}</div> : null}</td>
                      <td style={num}>{l.quantity}</td>
                      <td style={num}>{money(l.unitPrice)}</td>
                      <td style={{ ...td, opacity: 0.7, whiteSpace: 'nowrap' }}>{l.taxGroupCode} {l.taxRate}%</td>
                      <td style={num}>{money(l.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 14 }}>
                {/* totals */}
                <div>
                  <div style={row}><span>Subtotal</span><span>{money(bill.subtotal)}</span></div>
                  {bill.discountAmount > 0 && <div style={row}><span>Discount{bill.discount ? ` ${bill.discount.percent}%` : ''}</span><span>−{money(bill.discountAmount)}</span></div>}
                  {bill.serviceChargeAmt > 0 && <div style={row}><span>Service charge {bill.serviceChargePct}%</span><span>{money(bill.serviceChargeAmt)}</span></div>}
                  {bill.taxes.map((t) => <div key={`${t.sacHsnCode ?? ''}-${t.taxGroupCode}-${t.componentCode}`} style={{ ...row, opacity: 0.85 }}><span>{t.componentCode} {t.taxRate}% on {money(t.taxableAmount)}{t.sacHsnCode ? ` · SAC/HSN ${t.sacHsnCode}` : ''}</span><span>{money(t.taxAmount)}</span></div>)}
                  {bill.pricesIncludeTax && (
                    <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                      Prices include tax: items as charged {money(bill.chargedSubtotal ?? bill.subtotal)}, of which tax {money(bill.taxInclusiveAmount ?? 0)}. Amounts above are before tax.
                    </div>
                  )}
                  <div style={row}><span>Round-off</span><span>{money(bill.roundOffAmount)}</span></div>
                  <div style={{ ...row, fontWeight: 700, fontSize: 15, borderTop: '1px solid var(--border-color, #e5e7eb)', marginTop: 4, paddingTop: 8 }}><span>Total</span><span>{money(bill.totalAmount)}</span></div>
                  {bill.discount && (bill.discount.reason || bill.discount.aboveLimit) && (
                    <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>Discount {bill.discount.reason ? `— ${bill.discount.reason}` : ''}{bill.discount.aboveLimit ? ' · above the outlet limit, approved' : ''}</div>
                  )}
                </div>
                {/* receipts */}
                <div>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.6, marginBottom: 4 }}>Receipts</div>
                  {bill.payments.length === 0 && <div style={{ fontSize: 13, opacity: 0.6 }}>None</div>}
                  {bill.payments.map((p) => (
                    <div key={p.id} style={{ ...row, textDecoration: p.paymentStatus === 'REVERSED' ? 'line-through' : 'none', opacity: p.paymentStatus === 'REVERSED' ? 0.55 : 1 }}>
                      <span>{p.paymentMethod}{p.referenceNo ? ` · ${p.referenceNo}` : ''}<div style={{ fontSize: 11, opacity: 0.7 }}>{time(p.receivedAt)}{p.receivedByName ? ` · ${p.receivedByName}` : ''}</div></span>
                      <span>{money(p.amount)}</span>
                    </div>
                  ))}
                  <div style={{ ...row, fontWeight: 600, borderTop: '1px solid var(--border-color, #e5e7eb)', marginTop: 4, paddingTop: 8 }}><span>Paid</span><span>{money(bill.paidAmount)}</span></div>
                  {bill.balanceDue > 0 && <div style={{ ...row, color: 'var(--color-warning, #92400e)' }}><span>Balance due</span><span>{money(bill.balanceDue)}</span></div>}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={() => void print()} disabled={!bill}><IconSystem.print size={16} /> Print</button>
        </div>
      </div>
    </>
  );
};

export default DineOpsBillDetailModal;
